"use client";

import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "motion/react";
import Link from "next/link";

type ButtonMode = "filled" | "outline" | "ghost";
type ButtonSize = "sm" | "md" | "lg";
type ButtonEntrance = "slide" | "fade";

type BaseButtonProps = {
  children: React.ReactNode;
  className?: string;
  mode?: ButtonMode;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  fullWidth?: boolean;
  isRoundedFull?: boolean;
  entrance?: ButtonEntrance;
};

type ButtonAsButton = BaseButtonProps & {
  as?: "button";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: "button" | "submit" | "reset";
  href?: never;
  target?: never;
};

type ButtonAsLink = BaseButtonProps & {
  as: "link";
  href: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  onClick?: never;
  type?: never;
};

type ButtonProps = ButtonAsButton | ButtonAsLink;

const LoadingSpinner = () => (
  <motion.div
    className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
    animate={{ rotate: 360 }}
    transition={{
      duration: 1,
      repeat: Infinity,
      ease: "linear",
    }}
  />
);

const RippleEffect = ({ isActive }: { isActive: boolean }) => (
  <AnimatePresence>
    {isActive && (
      <motion.span
        className="absolute inset-0 rounded-lg bg-white/30"
        initial={{ scale: 0, opacity: 1 }}
        animate={{ scale: 1.5, opacity: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    )}
  </AnimatePresence>
);

const ShineEffect = ({ mode }: { mode: ButtonMode }) => {
  if (mode !== "filled") return null;

  return (
    <motion.div
      className="absolute inset-0 -bottom-1 -top-1 w-6 -skew-x-12 transform bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0"
      initial={{ x: "-100%", opacity: 0 }}
      animate={{ x: "calc(100% + 100px)", opacity: [0, 1, 0] }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        repeatDelay: 4,
        ease: "easeInOut",
      }}
    />
  );
};

const sizeClasses = {
  sm: "px-5 py-2 text-[15px] font-medium",
  md: "px-6 py-2.5 text-base font-medium lg:text-lg",
  lg: "px-8 py-3 text-lg font-semibold lg:text-xl",
};

const modeClasses = {
  filled: "bg-primary text-white shadow-lg border-2 border-primary",
  outline:
    "border-[0.5px] border-primary text-black-100 bg-transparent hover:bg-primary hover:text-white",
  ghost: "text-primary bg-transparent hover:bg-primary/10",
};

const hoverEffects = {
  filled: "hover:bg-green-2500 hover:border-green-2500 hover:shadow-xl",
  outline: "hover:shadow-lg",
  ghost: "",
};

const focusClasses =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2";
const activeClasses =
  "active:ring-2 active:ring-primary/30 active:ring-offset-1";
const disabledClasses =
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 disabled:shadow-none";

export function Button({
  children,
  className,
  mode = "filled",
  size = "md",
  disabled = false,
  loading = false,
  icon,
  iconPosition = "left",
  fullWidth = false,
  isRoundedFull = false,
  entrance = "slide",
  ...props
}: ButtonProps) {
  const [showRipple, setShowRipple] = useState(false);
  const rippleTimeout = useRef<NodeJS.Timeout | null>(null);
  const isFadeOnly = entrance === "fade";

  useEffect(() => {
    return () => {
      if (rippleTimeout.current) {
        clearTimeout(rippleTimeout.current);
      }
    };
  }, []);

  const baseClasses = cn(
    // Base styles
    "relative overflow-hidden transition-all duration-300 ease-out",
    isRoundedFull && "rounded-full",
    "inline-flex items-center justify-center gap-2 text-center font-medium",
    "transform select-none",

    // Size
    sizeClasses[size],

    // Mode
    modeClasses[mode],

    // Hover effects
    !disabled && !loading && hoverEffects[mode],

    // Focus
    focusClasses,

    // Active
    activeClasses,

    // Disabled
    disabledClasses,

    // Full width
    fullWidth && "w-full",

    // Custom className
    className
  );

  const handleInteraction = (
    e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>
  ) => {
    if (disabled || loading) return;

    if (rippleTimeout.current) {
      clearTimeout(rippleTimeout.current);
    }
    setShowRipple(true);
    rippleTimeout.current = setTimeout(() => setShowRipple(false), 800);

    if (props.as !== "link" && props.onClick) {
      props.onClick(e as React.MouseEvent<HTMLButtonElement>);
    }
  };

  const buttonContent = (
    <>
      <ShineEffect mode={mode} />

      <RippleEffect isActive={showRipple} />

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2"
          >
            <LoadingSpinner />
            <span>Loading...</span>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0, scale: isFadeOnly ? 1 : 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2"
          >
            {icon && iconPosition === "left" && (
              <motion.span
                initial={{ x: -8, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="flex items-center"
              >
                {icon}
              </motion.span>
            )}

            <motion.span
              initial={{ y: isFadeOnly ? 0 : 2, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              {children}
            </motion.span>

            {icon && iconPosition === "right" && (
              <motion.span
                initial={{ x: 8, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="flex items-center"
              >
                {icon}
              </motion.span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  const motionProps = {
    whileHover:
      !disabled && !loading
        ? {
            scale: 1.02,
            y: -2,
            transition: { duration: 0.2, ease: "easeOut" },
          }
        : {},
    whileTap:
      !disabled && !loading
        ? {
            scale: 0.98,
            y: 0,
            transition: { duration: 0.1 },
          }
        : {},
    initial: { opacity: 0, y: isFadeOnly ? 0 : 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, ease: "easeOut" },
    layout: !isFadeOnly,
  };

  if (props.as === "link") {
    return (
      <Link
        href={props.href}
        target={props.target}
        className={cn(isRoundedFull && "rounded-full")}
      >
        <motion.span
          className={baseClasses}
          onClick={handleInteraction}
          tabIndex={-1}
          {...motionProps}
        >
          {buttonContent}
        </motion.span>
      </Link>
    );
  }

  return (
    <motion.button
      type={props.type || "button"}
      onClick={handleInteraction}
      disabled={disabled || loading}
      className={baseClasses}
      {...motionProps}
    >
      {buttonContent}
    </motion.button>
  );
}
